import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { Box, Button, Chip, CircularProgress, Grid, Link, Paper, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowBack as BackIcon, Download as DownloadIcon, MenuBook as LensIcon } from "@mui/icons-material";
import { useAppDispatch } from "../../store/hooks";
import { setSnackAction } from "../../store/slices/snackSlice";
import lensService, { type Playbook, type PlaybookSource, type Provision, type SaveProvision } from "../../services/lensService";
import usePlaybook from "./hooks/usePlaybook";
import usePlaybookChangelog from "./hooks/usePlaybookChangelog";
import useLensMutation from "./hooks/useLensMutation";
import useStartGeneration from "./hooks/useStartGeneration";
import PlaybookHeader from "./PlaybookHeader";
import ProvisionList from "./ProvisionList";
import ProvisionEditor from "./ProvisionEditor";
import Changelog from "./Changelog";
import PlaybookGenerationStatus from "./PlaybookGenerationStatus";
import { generateResultMessage, latestDraftResult } from "./lensUtils";

const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(3) },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(4),
    },
    headerTitle: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
    sources: { marginTop: theme.spacing(-2), marginBottom: theme.spacing(3) },
    sourceRow: { display: "flex", alignItems: "center", gap: theme.spacing(1), color: theme.palette.text.secondary },
    empty: { padding: theme.spacing(6), textAlign: "center", color: "#999" },
    center: { display: "flex", justifyContent: "center", padding: theme.spacing(6) },
}));

// A draft is not used in reviews, and neither is anything in a disabled playbook.
const savedMessage = (saved: Provision, playbook: Playbook) => {
    if (saved.position.status === "draft") return "Saved as a draft — approve it to use it in reviews";
    return playbook.enabled ? "Saved — used in the next review" : "Saved — but this playbook is disabled, so no review uses it";
};

const PlaybookEditor = () => {
    const { classes } = useStyles();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { key } = useParams<{ key: string }>();
    const playbook = usePlaybook(key);
    const changelog = usePlaybookChangelog(key);
    const queryClient = useQueryClient();
    const [selectedKey, setSelectedKey] = useState<string | null>(null);

    const updatePlaybook = useLensMutation(lensService.updatePlaybook);
    const approveAll = useLensMutation(lensService.approveAll);
    const createProvision = useLensMutation(lensService.createProvision);
    const saveProvision = useLensMutation(lensService.saveProvision);
    const archiveProvision = useLensMutation(lensService.archiveProvision);
    const restoreProvision = useLensMutation(lensService.restoreProvision);
    const generation = useStartGeneration();
    const status = playbook.data?.generationStatus;
    const previousStatus = useRef(status);

    useEffect(() => {
        setSelectedKey(null);
    }, [key]);

    const snack = (message: string, severity: "success" | "warning" | "error" = "success") =>
        dispatch(setSnackAction(message, severity));

    // When a generation seen running on this page completes, the changelog has the row that says what it added.
    useEffect(() => {
        const was = previousStatus.current;
        previousStatus.current = status;
        if (!key || was !== "generating" || status !== "complete") return;

        const startedOn = playbook.data?.generationStartedOn;
        lensService
            .fetchChangelog(key)
            .then((changes) => {
                queryClient.setQueryData(["lens", "changelog", key], changes);
                queryClient.invalidateQueries({ queryKey: ["lens", "playbooks"] });
                const change = changes.find((c) => c.action === "ai-draft-from-precedent");
                const found = latestDraftResult(key, changes);
                if (!found || !change || (startedOn && new Date(change.changedOn) < new Date(startedOn))) {
                    snack("No new provisions found in that text — nothing was added");
                    return;
                }
                snack(generateResultMessage(found.result, found.source), found.result.flaggedCount > 0 ? "warning" : "success");
            })
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, status]);
    const backToList = () => navigate("/lens/playbooks");

    const back = (
        <Button variant="outlined" startIcon={<BackIcon />} onClick={backToList}>
            All playbooks
        </Button>
    );

    const current = playbook.data;
    const notFound = playbook.isError && isAxiosError(playbook.error) && playbook.error.response?.status === 404;

    if (!key || !current) {
        return (
            <Box className={classes.root}>
                <Box className={classes.header}>
                    <Box className={classes.headerTitle}>
                        <LensIcon style={{ color: "#7a5cf0", fontSize: 32 }} />
                        <Typography variant="h4">{notFound ? "Playbook not found" : "Playbook"}</Typography>
                    </Box>
                    {back}
                </Box>
                <Paper>
                    {playbook.isLoading ? (
                        <Box className={classes.center}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box className={classes.empty}>
                            <Typography variant="body1">
                                {notFound
                                    ? `No playbook "${key}" was found. It may have been archived, or the link may be wrong.`
                                    : "This playbook could not be loaded."}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Box>
        );
    }

    const provisions = current.provisions ?? [];
    const selected = provisions.find((p) => p.key === selectedKey) ?? null;
    const readOnly = current.archived;
    const isBusy =
        updatePlaybook.isPending ||
        approveAll.isPending ||
        createProvision.isPending ||
        archiveProvision.isPending ||
        restoreProvision.isPending;

    const update = (changes: { displayName?: string; enabled?: boolean; archived?: boolean }, message: string, after?: () => void) =>
        updatePlaybook.mutate(
            { key, changes },
            {
                onSuccess: () => {
                    snack(message);
                    after?.();
                },
            },
        );

    // The page follows the playbook's generation status once it shows this attempt.
    const startGeneration = (request: () => Promise<unknown>, failMessage: string) =>
        generation.start({
            key,
            request,
            started: (latest) => latest.generationStatus === "generating",
            failMessage,
            onStarted: (latest) => queryClient.setQueryData(["lens", "playbook", key], latest),
        });

    const draft = (text: string) =>
        startGeneration(() => lensService.draftFromPrecedent({ key, text }), "Could not draft provisions from that text.");

    const retry = () => startGeneration(() => lensService.retryGeneration(key), "Could not retry the generation.");

    const downloadSource = (source: PlaybookSource) =>
        lensService
            .downloadPlaybookSource(key, source.id, source.fileName)
            .catch(() => snack(`Could not download ${source.fileName}.`, "error"));

    const create = (provisionKey: string, name: string) =>
        createProvision.mutate(
            {
                key,
                provision: {
                    key: provisionKey,
                    name,
                    description: "",
                    absence: "recommended",
                    keywords: [],
                    position: {
                        requirement: "",
                        housePosition: "",
                        unacceptable: null,
                        absenceSeverity: "material",
                        citation: "",
                        modelClause: "",
                    },
                },
            },
            {
                onSuccess: (created) => {
                    setSelectedKey(created.key);
                    snack("Added as a draft — approve it to use it in reviews");
                },
            },
        );

    const save = (provision: SaveProvision) =>
        saveProvision.mutate({ key, provision }, { onSuccess: (saved) => snack(savedMessage(saved, current)) });

    const archive = () => {
        if (!selected) return;
        archiveProvision.mutate(
            { key, provisionKey: selected.key },
            {
                onSuccess: () => {
                    setSelectedKey(null);
                    snack("Provision archived");
                },
            },
        );
    };

    const restore = (provisionKey: string) =>
        restoreProvision.mutate(
            { key, provisionKey },
            {
                onSuccess: () => {
                    setSelectedKey(provisionKey);
                    snack("Provision restored");
                },
            },
        );

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Box className={classes.headerTitle}>
                    <LensIcon style={{ color: "#7a5cf0", fontSize: 32 }} />
                    <Typography variant="h4">{current.displayName}</Typography>
                </Box>
                {back}
            </Box>
            {(current.sources ?? []).length > 0 && (
                <Box className={classes.sources}>
                    <Typography variant="subtitle2" gutterBottom>
                        Source documents
                    </Typography>
                    {current.sources.map((source) => (
                        <Box key={source.id} className={classes.sourceRow}>
                            <DownloadIcon fontSize="small" />
                            <Link component="button" variant="body2" onClick={() => downloadSource(source)}>
                                {source.fileName}
                            </Link>
                            <Typography variant="caption">{source.role}</Typography>
                            {source.isPrimary && <Chip size="small" variant="outlined" label="primary" />}
                        </Box>
                    ))}
                </Box>
            )}

            <PlaybookHeader
                playbook={current}
                isBusy={isBusy}
                generating={generation.starting || current.generationStatus === "generating"}
                onRename={(displayName) => update({ displayName }, "Renamed")}
                onToggleEnabled={() =>
                    update({ enabled: !current.enabled }, current.enabled ? "Disabled — not offered for review" : "Enabled for review")
                }
                onToggleArchived={() =>
                    current.archived
                        ? update({ archived: false }, "Restored")
                        : update({ archived: true }, "Archived — no longer offered for review", backToList)
                }
                onApproveAll={() => {
                    const count = provisions.filter((p) => p.position.status === "draft" && !p.reviewNote).length;
                    approveAll.mutate(key, { onSuccess: () => snack(`Approved ${count} provision${count === 1 ? "" : "s"}`) });
                }}
                onDraftFromPrecedent={draft}
            />

            <PlaybookGenerationStatus playbook={current} retrying={generation.starting} onRetry={retry} />

            <Grid container spacing={4}>
                <Grid size={{ xs: 12, lg: 5 }}>
                    <ProvisionList
                        provisions={provisions}
                        archived={current.archivedProvisions ?? []}
                        selectedKey={selectedKey}
                        readOnly={readOnly}
                        isBusy={isBusy}
                        onSelect={setSelectedKey}
                        onCreate={create}
                        onRestore={restore}
                    />
                </Grid>
                <Grid size={{ xs: 12, lg: 7 }}>
                    {selected ? (
                        <ProvisionEditor
                            provision={selected}
                            readOnly={readOnly}
                            isSaving={saveProvision.isPending || archiveProvision.isPending}
                            onSave={save}
                            onArchive={archive}
                        />
                    ) : (
                        <Paper>
                            <Typography variant="body2" className={classes.empty}>
                                Select a provision to edit it.
                            </Typography>
                        </Paper>
                    )}
                </Grid>
            </Grid>

            <Changelog changes={changelog.data ?? []} isLoading={changelog.isLoading} />
        </Box>
    );
};

export default PlaybookEditor;
